import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { useUser } from "../../../context/UserContext";
import { useLanguage } from "../../../context/LanguageContext";
import { DEFAULT_STORE_ID } from "../../../firebase/firebase";
import {
  addToWishlist,
  removeFromWishlist,
  isInWishlist,
} from "../../../firebase/wishlist-functions";
import "./WishlistToggle.css";
import { toast } from "react-toastify";
import { Heart } from "lucide-react";
import AuthPage from "../../../pages/customer/auth/AuthPage";

/**
 * Wishlist Toggle Button Component
 * @param {Object} props - Component props
 * @param {Object} props.product - Product to add to wishlist
 * @param {string} props.variantId - Optional variant ID
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} - Wishlist button component
 */
const WishlistToggle = ({
  product,
  variantId = null,
  className = "",
  onRemove,
}) => {
  const { currentUser } = useUser();
  const { language } = useLanguage();
  const [showAuthModal, setShowAuthModal] = useState(false);

  // Translations for wishlist toggle
  const translations = {
    en: {
      addedToWishlist: "Added to wishlist",
      removedFromWishlist: "Removed from wishlist",
      failedToAdd: "Failed to add to wishlist",
      errorUpdating: "Error updating wishlist",
      addToWishlist: "Add to wishlist",
      removeFromWishlist: "Remove from wishlist",
    },
    ar: {
      addedToWishlist: "تمت الإضافة إلى قائمة الأمنيات",
      removedFromWishlist: "تمت الإزالة من قائمة الأمنيات",
      failedToAdd: "فشل في الإضافة إلى قائمة الأمنيات",
      errorUpdating: "خطأ في تحديث قائمة الأمنيات",
      addToWishlist: "أضف إلى قائمة الأمنيات",
      removeFromWishlist: "إزالة من قائمة الأمنيات",
    },
  };

  const t = translations[language] || translations.en;

  const [inWishlist, setInWishlist] = useState(false);
  const [loading, setLoading] = useState(false);
  const [wishlistItemId, setWishlistItemId] = useState(null);
  const [error, setError] = useState(null);

  // Check if product is in wishlist on component mount or when user/product/variant changes
  useEffect(() => {
    const checkWishlist = async () => {
      if (!currentUser || !product) return;

      try {
        // Use isInWishlist with returnItem=true to get the item ID
        const result = await isInWishlist(
          product.id,
          variantId,
          currentUser,
          true
        );
        if (result) {
          setInWishlist(true);
          setWishlistItemId(result.id);
        } else {
          setInWishlist(false);
          setWishlistItemId(null);
        }
      } catch (err) {
        console.error("Error checking wishlist status:", err);
      }
    };

    checkWishlist();
  }, [currentUser, product, variantId]);

  const toggleWishlist = async () => {
    if (!currentUser) {
      // Show auth modal if not logged in
      setShowAuthModal(true);
      return;
    }

    if (!product) {
      setError("Invalid product data");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (inWishlist) {
        // Remove from wishlist
        await removeFromWishlist(wishlistItemId, currentUser);
        setInWishlist(false);
        setWishlistItemId(null);

        if (onRemove) {
          onRemove();
        }
      } else {
        // Add to wishlist - ensure product has necessary properties
        const productWithStoreId = {
          ...product,
          storeId: product.storeId || DEFAULT_STORE_ID, // Ensure storeId is set
        };

        const result = await addToWishlist(
          productWithStoreId,
          variantId,
          currentUser
        );
        if (result.success) {
          setInWishlist(true);
          setWishlistItemId(result.id);

          if (!result.alreadyExists) {
          }
        } else {
          throw new Error(result.message || t.failedToAdd);
        }
      }
    } catch (err) {
      console.error("Error toggling wishlist:", err);
      setError(err.message || t.errorUpdating);
      toast.error(err.message || t.errorUpdating);
    } finally {
      setLoading(false);
    }
  };

  // Create portal for modal to render at document body level
  const modalContent = showAuthModal && !currentUser && (
    <div
      className="wishlist-auth-modal-overlay"
      onClick={() => setShowAuthModal(false)}
    >
      <div className="wishlist-auth-modal" onClick={(e) => e.stopPropagation()}>
        <AuthPage panelMode={false} onClose={() => setShowAuthModal(false)} />
      </div>
    </div>
  );

  return (
    <>
      <button
        className={`wishlist-button ${className} ${
          inWishlist ? "in-wishlist" : ""
        } ${loading ? "loading" : ""}`}
        onClick={toggleWishlist}
        disabled={loading}
        aria-label={inWishlist ? t.removeFromWishlist : t.addToWishlist}
        title={inWishlist ? t.removeFromWishlist : t.addToWishlist}
      >
        {loading ? (
          <span className="wishlist-loader"></span>
        ) : (
          <Heart
            size={20}
            strokeWidth={1.5}
            className={`wishlist-heart-icon ${inWishlist ? "filled" : ""}`}
            fill={inWishlist ? "currentColor" : "none"}
          />
        )}
      </button>

      {/* Render modal using Portal to bypass parent container constraints */}
      {modalContent && ReactDOM.createPortal(modalContent, document.body)}
    </>
  );
};

export default WishlistToggle;
